import { useEffect, useState } from "react"

/**
 * Best-effort device capability hints for tuning expensive
 * animations / rendering on lower-end hardware:
 *
 *   - saveData: visitor has Data Saver / Lite mode on. Treat as a
 *     direct ask to skip non-essential animation.
 *   - isMobile: coarse pointer (touchscreen). Mobile chips run hot
 *     and tend to have higher devicePixelRatio, so worth dialing
 *     pixel work down independently of CPU detection.
 *   - lowConcurrency: navigator.hardwareConcurrency < 4. Imperfect
 *     (Safari clamps to 8, etc.) but a useful floor signal.
 *
 * Defaults are all `false` on the server / first render so SSR and
 * client first paint match; real hints come in after mount.
 */
export default function useDeviceCapability() {
  const [hints, setHints] = useState({
    saveData: false,
    isMobile: false,
    lowConcurrency: false,
  })

  useEffect(() => {
    if (typeof navigator === "undefined") return
    const conn =
      navigator.connection ||
      navigator.mozConnection ||
      navigator.webkitConnection
    const isMobile =
      typeof window !== "undefined" && typeof window.matchMedia === "function"
        ? window.matchMedia("(pointer: coarse)").matches
        : false
    const hardwareConcurrency = navigator.hardwareConcurrency || 0
    const next = {
      saveData: !!conn?.saveData,
      isMobile,
      lowConcurrency: hardwareConcurrency > 0 && hardwareConcurrency < 4,
    }
    setHints(next)

    // saveData can be toggled at runtime — keep it in sync.
    if (!conn || typeof conn.addEventListener !== "function") return
    const handleChange = () => {
      setHints((prev) => ({ ...prev, saveData: !!conn.saveData }))
    }
    conn.addEventListener("change", handleChange)
    return () => conn.removeEventListener("change", handleChange)
  }, [])

  return hints
}
